import { useHandleResponse } from '@/utils/useHandleResponse';
import {
  BarChartOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Badge,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthProvider';
import {
  clearSelectedRecords,
  setSelectedRecords,
  updateProps,
} from '../../../../contextRedux/InterventionFile/InterventionFileModel';
import { setInterventionFiles } from '../../../../contextRedux/InterventionFile/InterventionFileStore';
import { InterventionFile } from '../../../../DTOS/InterventionFile/InterventionFile';
import { InterventionFileSystemType } from '../../../../DTOS/InterventionFile/InterventionFileSystemType';
import CompanyService from '../../../../services/company/CompanyService';
import type { InterventionFilePdfTemplate } from '../../../../services/interventionFile/InterventionFileService';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import { RootState } from '../../../../store';
import { canEditInterventionFile, isExternalTechnicianOnly, usePermission } from '../../../../utils/usePermission';
import InterventionFileDrawer from '../InterventionFileDrawer/InterventionFileInformation';
import './InterventionFileTable.css';

interface InterventionFileTableProps {}

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const InterventionFileTable: FC<InterventionFileTableProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.interventionFile.props.searchTerm);
  const searchTermType = useSelector((state: RootState) => state.models?.interventionFile.props.searchTermType);
  const skip = useSelector((state: RootState) => state.models?.interventionFile.props.skip);
  const take = useSelector((state: RootState) => state.models?.interventionFile.props.take);
  const refresh = useSelector((state: RootState) => state.models?.interventionFile.props.refresh);

  const reduxInterventionFile = useSelector((state: RootState) => state.models?.interventionFile);
  const interventionFiles = useSelector((state: RootState) => state.store?.interventionFile.data ?? []);
  const selectedRecords = useSelector((state: RootState) => state.models?.interventionFile.selectedRecords ?? []);
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  const currentPage = Math.floor(reduxInterventionFile.props.skip / reduxInterventionFile.props.take) + 1;
  const pageSizes = [10, 20, 30, 50];

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<InterventionFile | null>(null);
  const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);
  const [reportType, setReportType] = useState<'daily' | 'monthly'>('daily');
  const [pdfViewerVisible, setPdfViewerVisible] = useState<boolean>(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string>('file.pdf');
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState<InterventionFilePdfTemplate>('auto');
  const [sjReportModalVisible, setSjReportModalVisible] = useState<boolean>(false);
  const [sjReportSendError, setSjReportSendError] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [sjReportForm] = Form.useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  // Add new state variables for filters
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);

  // Date range filter (from - to)
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  // Periodicity filter: lunar, trimestrial, semestrial, anual, la cerere, solicitare, -
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<string | null>(null);
  const PERIODICITY_OPTIONS = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere', 'solicitare', '-'];

  const hasAdminPermission = usePermission(['dispatch', 'accountant', 'sales', 'senior_sales', 'manager']);
  const { profile } = useAuth();
  const isExternalOnly = isExternalTechnicianOnly(profile?.userPermission?.rols);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  // Update the query to include new filters
  const {
    data: _interventionFiles,
    refetch,
    isFetching: isInterventionFilesLoading,
  } = useQuery({
    queryKey: [
      'interventionFiles',
      searchTerm,
      searchTermType,
      skip,
      take,
      refresh,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedDateRange,
      selectedPeriodicity,
    ],
    queryFn: () =>
      InterventionFileService.search({
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        termen: searchTerm || undefined,
        termenType: searchTermType || undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
        startDate: selectedDateRange[0]?.format('YYYY-MM-DD') || undefined,
        endDate: selectedDateRange[1]?.format('YYYY-MM-DD') || undefined,
        periodicity: selectedPeriodicity || undefined,
        skip,
        take,
      }).then((data) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setInterventionFiles(data?.response));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies', isExternalOnly ? 'external' : 'all'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !isExternalOnly,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string, search: string) => {
      dispatch(updateProps({ field: search, value: value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1000),
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTerm');
    },
    [searchTerm],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTermType');
    },
    [searchTermType],
  );

  const { mutate: removeInterventionFile, isPending: isRemoving } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.upsert(formData?.id, formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }

      handleResponse(data, 'Succes!');
      refetch(); // Refresh the list
    },
    onError: (error: any) => {
      console.error('Error processing your request:', error);
    },
  });

  // Wrapper function to call mutate
  const handleRemove = (id: string) => {
    const interventionFile = interventionFiles.find((i: InterventionFile) => i.id == id);
    removeInterventionFile({ ...interventionFile, isActive: false });
  };

  const handleModalInformationOpen = (record: InterventionFile) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  const sanitizeFilename = (value: string) =>
    value
      .replace(/[\\/:*?"<>|]/g, ' ') // Windows forbidden chars
      .replace(/\s+/g, ' ')
      .trim();

  const buildPdfFilename = (record: any) => {
    const companyName = record?.metadata?.company?.name ?? record?.company?.name ?? 'Client';
    const workPlaceName = record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Workplace';
    const fileNumber = record?.number ?? record?.metadata?.number ?? '';
    const base =
      !companyName || !workPlaceName || !fileNumber
        ? (record?.id ?? 'file')
        : `${companyName} ${workPlaceName} ${fileNumber}`;
    return `${sanitizeFilename(base)}.pdf`;
  };

  const { mutate: printInterventionFile, isPending: isPrinting } = useMutation<
    Response,
    Error,
    { record: any; action: 'view' | 'download' | 'newTab'; targetWindow?: Window | null }
  >({
    mutationFn: async ({ record, action }) => {
      // Always fetch the file with authentication headers
      const response = await InterventionFileService.print(record.id, selectedPdfTemplate);
      return response;
    },
    onSuccess: async (response, { action, targetWindow, record }) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
        }
      }
      if (action === 'download') {
        filename = buildPdfFilename(record);
      }

      if (action === 'view') {
        // Create a blob URL and open it in the secure viewer modal
        const blobUrl = window.URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
        setPdfFilename(buildPdfFilename(record));
        setPdfViewerVisible(true);
      } else if (action === 'newTab') {
        const blobUrl = window.URL.createObjectURL(blob);
        const filename = buildPdfFilename(record);
        const escapedFilename = filename.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        // Minimal HTML wrapper: iframe full viewport + overlay button positioned over the PDF viewer's download icon (top-right).
        const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapedFilename}</title><style>body{margin:0;padding:0;overflow:hidden}.pdf{position:fixed;top:0;left:0;right:0;bottom:0;width:100%;height:100vh;border:none}.dl{position:fixed;top:5px;right:85px;z-index:9999;width:40px;height:40px;padding:0;border:none;border-radius:50%;background:rgba(50,50,50,0.85);cursor:pointer;display:flex;align-items:center;justify-content:center}.dl:hover{background:rgba(70,70,70,0.9)}.dl svg{color:#fff;pointer-events:none}</style></head><body><iframe class="pdf" src="${blobUrl}" title="PDF"></iframe><a class="dl" href="${blobUrl}" download="${escapedFilename}" title="Descarcă PDF"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a></body></html>`;
        const htmlBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = htmlBlobUrl;
        } else {
          window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
        }
        setTimeout(() => window.URL.revokeObjectURL(htmlBlobUrl), 60_000);
      } else if (action === 'download') {
        saveBlobCrossPlatform(blob, filename);
      }
    },
    onError: (error) => {
      console.error('Error processing your request:', error);
      message.error('Eroare la încărcarea fișierului PDF.');
    },
  });

  const { mutate: downloadJournal, isPending: isDownloadingJournal } = useMutation({
    mutationFn: async ({ record }: { record: any }) => {
      try {
        const response = await InterventionFileService.downloadAllJournals(record.workPlace.id, record.deliveryDate);

        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage = errorData.errors?.[0] || 'Nu s-au găsit jurnale pentru acest punct de lucru';
          message.error(errorMessage);
          throw new Error(errorMessage);
        }

        return response;
      } catch (error) {
        console.error('Error fetching journals:', error);
        message.error('A apărut o eroare la descărcarea jurnalelor. Vă rugăm să încercați din nou.');
        throw error;
      }
    },
    onSuccess: async (response) => {
      try {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'jurnale.zip';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        const blob = await response.blob();
        saveBlobCrossPlatform(blob, filename);

        message.success('Jurnalele au fost descărcate cu succes!');
      } catch (error) {
        console.error('Error processing download:', error);
        message.error('A apărut o eroare la procesarea descărcării jurnalelor.');
      }
    },
    onError: (error: Error) => {
      console.error('Error downloading journals:', error);
      message.error('Nu s-au găsit jurnale pentru acest punct de lucru sau a apărut o eroare la descărcare.');
    },
  });

  // Wrapper function to call mutate with 'download' action
  const printToPdf = (record: any) => {
    printInterventionFile({ record, action: 'download' });
  };

  const openPdfInModal = (record: any) => {
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      printInterventionFile({ record, action: 'download' });
      return;
    }
    printInterventionFile({ record, action: 'view' });
  };

  // Wrapper function to call mutate with 'view' action
  const openPdfInNewTab = (record: any) => {
    // In APK mode, treat open in new tab as download since APK can't handle browser tabs
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      printInterventionFile({ record, action: 'download' });
      return;
    }

    if (!record?.id) {
      message.error('ID-ul fișei de intervenție lipsește.');
      return;
    }
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteți ferestrele pop-up pentru a deschide PDF-ul într-un tab nou.');
      return;
    }
    printInterventionFile({ record, action: 'newTab', targetWindow });
    try {
      targetWindow.opener = null;
    } catch {}
  };

  // Handle PDF viewer modal close
  const handlePdfViewerClose = () => {
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfViewerVisible(false);
    setPdfFilename('file.pdf');
  };

  // Handle download from PDF viewer
  const handleDownloadFromViewer = () => {
    if (pdfBlobUrl) {
      fetch(pdfBlobUrl)
        .then((res) => res.blob())
        .then((blob) => {
          saveBlobCrossPlatform(blob, pdfFilename);
        })
        .catch((error) => {
          console.error('Error downloading PDF:', error);
          message.error('Eroare la descărcarea fișierului PDF.');
        });
    }
  };

  // Add handlers for new filters
  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setSelectedDateRange(dates ?? [null, null]);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handlePeriodicityChange = (value: string | null) => {
    setSelectedPeriodicity(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  // Report generation handlers
  const showReportModal = (type: 'daily' | 'monthly') => {
    setReportType(type);
    form.setFieldsValue({ reportDate: dayjs() });
    setReportModalVisible(true);
  };

  const handleReportModalCancel = () => {
    setReportModalVisible(false);
  };

  const { mutate: generateReport, isPending: isGeneratingReport } = useMutation({
    mutationFn: async ({ type, date }: { type: 'daily' | 'monthly'; date: Date }) => {
      try {
        if (type === 'daily') {
          const response = await InterventionFileService.generateDailyReport(date);
          return response;
        } else {
          const response = await InterventionFileService.generateMonthlyReport(date);
          return response;
        }
      } catch (error) {
        console.error(`Error generating ${type} report:`, error);
        throw error;
      }
    },
    onSuccess: (data) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }

      message.success(`Raportul ${reportType === 'daily' ? 'zilnic' : 'lunar'} a fost generat cu succes!`);
      setReportModalVisible(false);
    },
    onError: (error) => {
      console.error('Error generating report:', error);
      message.error('A apărut o eroare la generarea raportului. Vă rugăm să încercați din nou.');
    },
  });

  const {
    data: sjReportPreview,
    isFetching: isSjReportPreviewLoading,
    dataUpdatedAt: sjReportPreviewUpdatedAt,
  } = useQuery({
    queryKey: ['monthlySJReportPreview'],
    queryFn: () => InterventionFileService.getMonthlySJReportPreview(),
    enabled: sjReportModalVisible,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const { mutate: sendMonthlySJReport, isPending: isSendingSJReport } = useMutation({
    mutationFn: ({ recipients, cc }: { recipients: string[]; cc: string[] }) =>
      InterventionFileService.sendMonthlySJReport(recipients, cc),
    onSuccess: (data) => {
      if (data?.errors && data.errors.length > 0) {
        setSjReportSendError(data.errors.join(' '));
        message.error(data.errors[0]);
        return;
      }

      message.success(data?.response || 'Raportul SJ a fost trimis cu succes!');
      closeSjReportModal();
    },
    onError: (error) => {
      console.error('Error sending SJ report:', error);
      const errorMessage = 'A apărut o eroare la trimiterea raportului SJ. Vă rugăm să încercați din nou.';
      setSjReportSendError(errorMessage);
      message.error(errorMessage);
    },
  });

  const closeSjReportModal = () => {
    setSjReportModalVisible(false);
    setSjReportSendError(null);
  };

  const handleSendSjReport = () => {
    sjReportForm.validateFields().then((values) => {
      setSjReportSendError(null);
      sendMonthlySJReport({ recipients: values.recipients ?? [], cc: values.cc ?? [] });
    });
  };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmails = (_: unknown, emails: string[] | undefined) => {
    const invalid = (emails ?? []).filter((email) => !EMAIL_PATTERN.test(email.trim()));
    return invalid.length ? Promise.reject(new Error(`Adrese invalide: ${invalid.join(', ')}`)) : Promise.resolve();
  };

  const handleGenerateReport = () => {
    form.validateFields().then((values) => {
      if (values.reportDate) {
        // Get the date in YYYY-MM-DD format to avoid timezone issues
        const formattedDate = values.reportDate.format('YYYY-MM-DD');
        // Create a new Date object with the formatted date string
        const dateObj = new Date(formattedDate);

        generateReport({
          type: reportType,
          date: dateObj,
        });
      }
    });
  };

  // Handlers for selection management
  const handleClearSelection = () => {
    dispatch(clearSelectedRecords());
  };

  const handleBulkDownload = () => {
    if (!selectedRecords.length) {
      message.warning('Vă rugăm să selectați cel puțin o fișă de intervenție pentru descărcare.');
      return;
    }

    selectedRecords.forEach((record: any) => {
      printInterventionFile({ record, action: 'download' });
    });
  };
  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedRecords.map((record: any) => record.id),
    onChange: (_selectedRowKeys: React.Key[], selectedRows: any[]) => {
      // Get current page records
      const currentPageRecords = interventionFiles;

      // Create a map of existing selections
      const existingSelectionsMap = new Map();
      selectedRecords.forEach((record: any) => {
        existingSelectionsMap.set(record.id, record);
      });

      // Update selections for current page
      currentPageRecords.forEach((record: any) => {
        if (selectedRows.some((selected: any) => selected.id === record.id)) {
          existingSelectionsMap.set(record.id, record);
        } else {
          existingSelectionsMap.delete(record.id);
        }
      });

      // Convert map back to array
      const updatedSelections = Array.from(existingSelectionsMap.values());
      dispatch(setSelectedRecords(updatedSelections));
    },
    getCheckboxProps: () => ({
      disabled: false,
    }),
    // Add select all functionality
    selections: [
      {
        key: 'selectAll',
        text: 'Selectează toate',
        onSelect: () => {
          const currentPageRecords = interventionFiles;
          const existingSelectionsMap = new Map();

          // Add existing selections
          selectedRecords.forEach((record: any) => {
            existingSelectionsMap.set(record.id, record);
          });

          // Add all current page records
          currentPageRecords.forEach((record: any) => {
            existingSelectionsMap.set(record.id, record);
          });

          const updatedSelections = Array.from(existingSelectionsMap.values());
          dispatch(setSelectedRecords(updatedSelections));
        },
      },
      {
        key: 'clearAll',
        text: 'Șterge toate selecțiile',
        onSelect: () => {
          dispatch(clearSelectedRecords());
        },
      },
    ],
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxInterventionFile.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes?.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: any[] = useMemo(
    () =>
      [
        {
          title: 'Numar',
          dataIndex: '',
          key: '',
          render: (record: any) => (
            <Tag
              color={'blue'}
              style={{ cursor: 'pointer' }}
              onClick={() => (isExternalOnly ? {} : openPdfInNewTab(record))}
            >
              {record?.number}
            </Tag>
          ),
        },
        {
          title: 'Tip',
          dataIndex: 'type',
          key: 'type',
        },
        {
          title: 'Firma',
          key: 'company',
          render: (_: any, record: any) => {
            const company = record?.metadata?.company ?? record?.company;
            return (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>
                {company?.name?.length > 15 ? company.name : company?.name}
              </div>
            );
          },
        },
        {
          title: 'Punct de lucru',
          dataIndex: '',
          key: 'workPlaceName',
          render: (record: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              <b>
                {(record?.metadata?.workPlace?.name?.length ?? record?.workPlace?.name?.length > 30)
                  ? (record?.metadata?.workPlace?.name ?? record?.workPlace?.name)
                  : (record?.metadata?.workPlace?.name ?? record?.workPlace?.name)}
              </b>
            </div>
          ),
        },
        {
          title: 'Adresa',
          dataIndex: '',
          key: 'workPlace',
          render: (record: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              {(record?.metadata?.address?.length ?? record?.address?.length > 30)
                ? (record?.metadata?.workPlace?.address ?? record?.workPlace?.address)
                : (record?.metadata?.workPlace?.address ?? record?.workPlace?.address)}
            </div>
          ),
        },
        {
          title: 'Periodicitate',
          dataIndex: '',
          key: '',
          render: (interventionFile: any) => (
            <>
              <div style={{ textAlign: 'center' }}>
                <b>
                  {interventionFile?.mentenancePlanning != undefined
                    ? interventionFile?.mentenancePlanning?.periodicity
                    : interventionFile?.requestFileId != undefined
                      ? 'solicitare'
                      : ' - '}
                </b>
              </div>
            </>
          ),
        },
        {
          title: 'Sistem',
          dataIndex: 'interventionFileSystemTypes',
          key: 'interventionFileSystemTypes',
          render: (interventionFileSystemType: InterventionFileSystemType[]) => (
            <>
              {interventionFileSystemType && interventionFileSystemType.length > 0 && (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>
                  <b>
                    {interventionFileSystemType
                      ?.map((type: any) =>
                        type.systemType.name.length > 20 ? type.systemType.name : type.systemType.name,
                      )
                      ?.map((name: string) => `[${name}]`)
                      .join('')}
                  </b>
                </div>
              )}
            </>
          ),
        },
        {
          title: 'Tehnician',
          dataIndex: 'helionEngineers',
          key: 'helionEngineers',
          render: (helionEngineers: string[]) => {
            if (!helionEngineers || helionEngineers.length === 0) {
              return <div>----</div>;
            }

            // Deduplicate engineers by userId to prevent showing the same engineer multiple times
            const uniqueEngineers = helionEngineers.filter(
              (engineer: any, index: number, self: any[]) =>
                index ===
                self.findIndex((e: any) => e?.userId === engineer?.userId || e?.user?.id === engineer?.user?.id),
            );

            return (
              <>
                {uniqueEngineers.map((engineer: any, index: number) => (
                  <div key={engineer?.userId || engineer?.user?.id || index}>{'[' + engineer?.user?.name + ']'}</div>
                ))}
              </>
            );
          },
        },
        {
          title: 'Data semnare',
          dataIndex: 'deliveryDate',
          key: 'deliveryDate',
          render: (deliveryDate: string) => moment(deliveryDate).format('DD-MM-YYYY'),
        },
        {
          title: 'Status',
          dataIndex: '',
          key: 'isActive',
          render: (record: any) => (
            <div>
              <Tag color={record?.isDraft == true ? 'orange' : record?.isActive == true ? 'blue' : 'red'}>
                {record?.isDraft ? 'Draft' : record?.isActive ? 'Activ' : 'Inactiv'}
              </Tag>
            </div>
          ),
        },
        profile?.userPermission?.rols?.includes('accountant') && {
          title: 'Cost interventie',
          dataIndex: '',
          key: 'totalCost',
          render: (record: any) =>
            record?.requiresPayment == true ? (
              <Tag color={'red'}>
                {record?.totalCost}/{record.isPaidOnTeam == true ? 'echipa' : 'tehnician'}
              </Tag>
            ) : (
              <Tag color={'blue'}>Gratis</Tag>
            ),
        },
        {
          title: 'Actiuni',
          dataIndex: '',
          key: 'actions',
          align: 'center',
          render: (record: InterventionFile) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {canEditInterventionFile(record, profile) ? (
                <Tooltip title='Modifica'>
                  <Button
                    style={{ marginRight: 20 }}
                    icon={<EditOutlined />}
                    onClick={() => window.open(`/service-maintenance/interventionFile/edit?id=${record?.id}`, '_blank')}
                  />
                </Tooltip>
              ) : (
                <Tooltip title='Nu aveți permisiunea de a modifica această fișă de intervenție'>
                  <Button style={{ marginRight: 20 }} icon={<EditOutlined />} disabled />
                </Tooltip>
              )}
              <Tooltip title='Previzualizare PDF'>
                <Button style={{ marginRight: 20 }} icon={<FilePdfOutlined />} onClick={() => openPdfInModal(record)} />
              </Tooltip>
              <Tooltip title='Descarca PDF'>
                <Button
                  icon={<DownloadOutlined />}
                  style={{ marginRight: 20 }}
                  disabled={isExternalOnly}
                  onClick={() => printToPdf(record)}
                />
              </Tooltip>
              {hasAdminPermission && (
                <>
                  <Tooltip title='Descarca Jurnale de service'>
                    <Button
                      icon={<FileTextOutlined />}
                      style={{ marginRight: 20 }}
                      onClick={() => downloadJournal({ record })}
                      loading={isDownloadingJournal}
                    />
                  </Tooltip>
                  <Tooltip title='Sterge'>
                    <Button
                      icon={<DeleteOutlined />}
                      style={{ marginRight: 20 }}
                      onClick={() => handleRemove(record.id)}
                    />
                  </Tooltip>
                </>
              )}
            </div>
          ),
        },
      ].filter(Boolean),
    [interventionFiles, isDownloadingJournal, selectedRecords, selectedPdfTemplate],
  );

  if (isRemoving || isInterventionFilesLoading || isPrinting || isCompaniesLoading || isSystemTypesLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <div className='intervention-file-table-page'>
      <div>
        <h1>Fise Helion SA</h1>
      </div>
      {(profile?.userPermission?.rols ?? [])?.includes('admin') && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col>
            <Button icon={<CalendarOutlined />} onClick={() => showReportModal('daily')}>
              Genereaza Raport Zilnic
            </Button>
          </Col>
          <Col>
            <Button icon={<BarChartOutlined />} onClick={() => showReportModal('monthly')}>
              Genereaza Raport Lunar
            </Button>
          </Col>
          <Col>
            <Button icon={<SendOutlined />} onClick={() => setSjReportModalVisible(true)}>
              Trimitere Raport SJ
            </Button>
          </Col>
        </Row>
      )}

      {(profile?.userPermission?.rols ?? [])?.includes('accountant') && (
        <Row>
          <Col>
            <Select
              value={selectedPdfTemplate}
              style={{ width: 220 }}
              onChange={(value) => setSelectedPdfTemplate(value)}
              options={[
                { value: 'auto', label: 'Template PDF: Automat' },
                { value: 'helion', label: 'Template PDF: Helion' },
                { value: 'cxc', label: 'Template PDF: CXC' },
              ]}
            />
          </Col>
        </Row>
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 20, justifyContent: 'space-between', alignItems: 'center' }}>
        <Col>
          <div className='action-buttons-container' style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Tooltip title='Ștergeți toate selecțiile'>
              <Button
                type='default'
                onClick={handleClearSelection}
                disabled={selectedRecords.length === 0}
                icon={<DeleteOutlined />}
                shape='circle'
              />
            </Tooltip>
            <div className='bulk-download-button'>
              <Tooltip title={`Descarca fișele de intervenție selectate (${selectedRecords.length})`}>
                <Button
                  type='primary'
                  onClick={handleBulkDownload}
                  disabled={selectedRecords.length === 0}
                  loading={isPrinting}
                  icon={<DownloadOutlined />}
                  shape='circle'
                />
              </Tooltip>
              {selectedRecords.length > 0 && (
                <Badge count={selectedRecords.length} color='blue' className='bulk-download-badge' />
              )}
            </div>
          </div>
        </Col>
        <Col>
          {!isExternalOnly && (
            <Tooltip title='Creeaza fisa de interventie' trigger={'hover'} placement='left'>
              <Button
                type='primary'
                onClick={() => window.open(`/service-maintenance/interventionFile/new`, '_blank')}
                icon={<PlusOutlined />}
                shape='circle'
                disabled={profile?.userPermission?.rols?.includes('dispatcher') ? false : true}
              />
            </Tooltip>
          )}
        </Col>
      </Row>
      <InterventionFileDrawer
        handleModalInformationClose={handleModalInformationClose}
        modalInformation={modalInformation}
        selectedRecord={selectedRecord}
      />
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus={true}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces?.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes?.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>{' '}
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder='Filtreaza dupa tip'
            value={searchTermType || undefined}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
            allowClear
          >
            <Select.Option value='Service [cu contract]'>Service [cu contract]</Select.Option>
            <Select.Option value='Service [fara contract]'>Service [fara contract]</Select.Option>
            <Select.Option value='Garantie'>Garantie</Select.Option>
            <Select.Option value='Mentenanta periodica'>Mentenanta periodica</Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder='Filtreaza dupa periodicitate'
            value={selectedPeriodicity}
            onChange={handlePeriodicityChange}
            style={{ width: '100%' }}
            allowClear
          >
            {PERIODICITY_OPTIONS.map((p) => (
              <Select.Option key={p} value={p}>
                {p}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <DatePicker.RangePicker
            placeholder={['Data de la', 'Data pana la']}
            value={selectedDateRange[0] && selectedDateRange[1] ? selectedDateRange : null}
            onChange={(dates) => handleDateRangeChange(dates ?? [null, null])}
            style={{ width: '100%' }}
            format='DD-MM-YYYY'
            allowClear
          />
        </Col>
      </Row>
      <div className='intervention-file-table-scroll-wrapper'>
        <Table
          rowSelection={rowSelection}
          dataSource={interventionFiles}
          columns={columns}
          rowKey='id'
          pagination={paginationConfig}
          scroll={{ x: 'max-content' }}
          style={{ boxShadow: '0px 0px 10px rgba(0,0,0,0.2)' }}
          rowClassName={(record: any) => {
            return record?.interventionFilePricing?.isFree == false ? 'error-row' : '';
          }}
        />
      </div>
      {/* Report Generation Modal */}
      {(profile?.userPermission?.rols ?? [])?.includes('admin') && (
        <Modal
          title={`Genereaza Raport ${reportType === 'daily' ? 'Zilnic' : 'Lunar'}`}
          open={reportModalVisible}
          onCancel={handleReportModalCancel}
          footer={[
            <Button key='cancel' onClick={handleReportModalCancel}>
              Anuleaza
            </Button>,
            <Button key='generate' type='primary' onClick={handleGenerateReport} loading={isGeneratingReport}>
              Genereaza
            </Button>,
          ]}
        >
          <Form form={form} layout='vertical'>
            <Form.Item
              name='reportDate'
              label='Data Raport'
              rules={[{ required: true, message: 'Te rog selecteaza data raportului' }]}
            >
              <DatePicker style={{ width: '100%' }} format='DD-MM-YYYY' />
            </Form.Item>
            <div>
              <p>Raportul va fi trimis prin email</p>
            </div>
          </Form>
        </Modal>
      )}
      {/* SJ Report Modal */}
      {(profile?.userPermission?.rols ?? [])?.includes('admin') && (
        <Modal
          title='Trimitere Raport SJ'
          open={sjReportModalVisible}
          onCancel={closeSjReportModal}
          destroyOnClose
          footer={[
            <Button key='cancel' onClick={closeSjReportModal}>
              Anuleaza
            </Button>,
            <Button
              key='send'
              type='primary'
              icon={<SendOutlined />}
              onClick={handleSendSjReport}
              loading={isSendingSJReport}
              disabled={isSjReportPreviewLoading || !sjReportPreview?.response?.scannCount}
            >
              Trimite
            </Button>,
          ]}
        >
          {isSjReportPreviewLoading ? (
            <Spin />
          ) : sjReportPreview?.errors?.length ? (
            <Alert type='error' showIcon message={sjReportPreview.errors[0]} />
          ) : (
            sjReportPreview?.response && (
              <>
                <Form
                  form={sjReportForm}
                  key={sjReportPreviewUpdatedAt}
                  layout='vertical'
                  initialValues={{
                    recipients: sjReportPreview.response.recipients ?? [],
                    cc: sjReportPreview.response.cc ?? [],
                  }}
                >
                  <Form.Item
                    name='recipients'
                    label='Catre'
                    extra='Scrieti o adresa si apasati Enter pentru a o adauga.'
                    rules={[
                      { required: true, type: 'array', min: 1, message: 'Adaugati cel putin un destinatar' },
                      { validator: validateEmails },
                    ]}
                  >
                    <Select mode='tags' open={false} tokenSeparators={[',', ';', ' ']} placeholder='email@exemplu.ro' />
                  </Form.Item>
                  <Form.Item name='cc' label='CC' rules={[{ validator: validateEmails }]}>
                    <Select mode='tags' open={false} tokenSeparators={[',', ';', ' ']} placeholder='email@exemplu.ro' />
                  </Form.Item>
                </Form>
                <Descriptions column={1} bordered size='small'>
                  <Descriptions.Item label='Luna'>
                    {sjReportPreview.response.month} {sjReportPreview.response.year}
                  </Descriptions.Item>
                  <Descriptions.Item label='Dispozitive scanate'>
                    {sjReportPreview.response.scannCount === 0 ? (
                      <Tag color='red'>0 - nu exista date, raportul nu poate fi trimis</Tag>
                    ) : (
                      sjReportPreview.response.scannCount
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label='Fise de interventie (corective)'>
                    {sjReportPreview.response.interventionFileCount}
                  </Descriptions.Item>
                </Descriptions>
                {sjReportSendError && (
                  <Alert
                    type='error'
                    showIcon
                    style={{ marginTop: 16 }}
                    message='Raportul nu a fost trimis'
                    description={sjReportSendError}
                  />
                )}
              </>
            )
          )}
        </Modal>
      )}
      {/* PDF Viewer Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Previzualizare PDF</span>
          </div>
        }
        open={pdfViewerVisible}
        onCancel={handlePdfViewerClose}
        footer={[
          <Button key='close' onClick={handlePdfViewerClose}>
            Închide
          </Button>,
          <Button key='download' type='primary' icon={<DownloadOutlined />} onClick={handleDownloadFromViewer}>
            Descarcă PDF
          </Button>,
        ]}
        width='90%'
        style={{ top: 20 }}
        bodyStyle={{ height: 'calc(100vh - 200px)', padding: 0 }}
        destroyOnClose
      >
        {pdfBlobUrl && (
          <iframe
            src={pdfBlobUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title='PDF Viewer'
          />
        )}
      </Modal>
    </div>
  );
};

export default InterventionFileTable;
